import React, { Suspense, useEffect } from "react";
import { Route, Routes } from "react-router-dom";
import EventAPI from "../components/common/EventAPI";
import Logout from "../components/common/Logout";
import SkeletonLoader from "../components/common/SkeletonLoader";
import {
  REDIRECT_DOMAINS,
  SUBDOMAIN_PREFIXES,
} from "../assets/const/redirect-domains.const";

// Shared components loaded eagerly for reuse
import InternetSalesPage from "../components/templates/broadband/pages/InternetSalesPage";
import AdditionalDevicesPage from "../components/templates/broadband/pages/AdditionalDevicesPage";
import BroadbandCheckoutPage from "../components/templates/broadband/pages/BroadbandCheckoutPage";
import GenericError from "../components/common/GenericError";
import ClickCapturePageC9RecieveRoute from "../components/templates/wifi/pages/ClickCapturePageC9RecieveRoute";
import NetworkAccessDenied from "../components/templates/broadband/pages/NetworkAccessDeniedPage";

// Lazy loaded routes (reverted)
const SelfcareRoute = React.lazy(
  () => import("../components/templates/selfcare/SelfcareRoute")
);
const WifiRoute = React.lazy(
  () => import("../components/templates/wifi/WifiRoute")
);
const BroadbandRoute = React.lazy(
  () => import("../components/templates/broadband/BroadbandRoute")
);
const PrivateServicesRoute = React.lazy(
  () => import("../components/templates/private-services/PrivateServicesRoute")
);
const Venues = React.lazy(
  () => import("../components/templates/wifi/pages/Venues")
);

// Export shared components for reuse
export { InternetSalesPage, AdditionalDevicesPage, BroadbandCheckoutPage };

const RouteConfig: React.FC = () => {
  // Get the hostname to determine which component to render
  const hostname = window.location.hostname;
  const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";

  // Redirect root paths for specific subdomains to Boingo pages
  useEffect(() => {
    const currentPath = window.location.pathname;
    const isRootPath = currentPath === "/" || currentPath === "";

    if (!isLocalhost && isRootPath) {
      if (hostname.startsWith(SUBDOMAIN_PREFIXES.RETAIL)) {
        window.location.href = REDIRECT_DOMAINS.RETAIL;
      } else if (hostname.startsWith(SUBDOMAIN_PREFIXES.MILITARY)) {
        window.location.href = REDIRECT_DOMAINS.MILITARY;
      } else if (hostname.startsWith(SUBDOMAIN_PREFIXES.PS)) {
        window.location.href = REDIRECT_DOMAINS.PS;
      }
    }
  }, [hostname, isLocalhost]);

  return (
    <>
      <EventAPI />
      <Routes>
        <Route path="/error" element={<GenericError />} />
        {/* Global Venues Page - Top Level */}
        <Route
          path="/global/venues"
          element={
            <Suspense fallback={<div>Loading Venues...</div>}>
              {" "}
              <Venues />
            </Suspense>
          }
        />
        <Route path="/:template/:venue_id/goodbye" element={<Logout />} />

        {/* Localhost development routes */}
        {isLocalhost && (
          <>
            <Route
              path="/retail/clickcapture.aspx"
              element={<ClickCapturePageC9RecieveRoute />}
            />
            {/* WifiRoute with specific loader */}
            <Route
              path="/retail/clickcapture"
              element={
                <Suspense fallback={<SkeletonLoader count={1} />}>
                  <WifiRoute />
                </Suspense>
              }
            />
            <Route
              path="/retail/:venue_id/*"
              element={
                <Suspense fallback={<SkeletonLoader count={3} />}>
                  <WifiRoute />
                </Suspense>
              }
            />

            {/* Other lazy routes with null fallback */}
            <Route
              path="/military/networkaccessdenied"
              element={<NetworkAccessDenied />}
            />
            <Route
              path="/military/:venue_id/*"
              element={
                <Suspense fallback={null}>
                  <BroadbandRoute />
                </Suspense>
              }
            />
            <Route
              path="/ps/:venue_id/*"
              element={
                <Suspense fallback={null}>
                  <PrivateServicesRoute />
                </Suspense>
              }
            />
            <Route
              path="*"
              element={
                <Suspense fallback={null}>
                  <SelfcareRoute />
                </Suspense>
              }
            />
          </>
        )}

        {/* Production/ GCP routes */}
        {!isLocalhost && (
          <>
            {/* Route for C9 ClickCapture Redirector in Production */}
            {hostname.startsWith("retail") && (
              <Route
                path="/clickcapture.aspx"
                element={<ClickCapturePageC9RecieveRoute />}
              />
            )}
            {hostname.startsWith("retail") && (
              <>
                <Route
                  path="/clickcapture"
                  element={
                    <Suspense fallback={<SkeletonLoader count={1} />}>
                      <WifiRoute />
                    </Suspense>
                  }
                />
                <Route
                  path="/:venue_id/*"
                  element={
                    <Suspense fallback={<SkeletonLoader count={3} />}>
                      <WifiRoute />
                    </Suspense>
                  }
                />
              </>
            )}
            {(hostname.startsWith("military") ||
              hostname.startsWith("bulk") ||
              hostname.startsWith("broadband")) && (
              <>
                <Route
                  path="/networkaccessdenied"
                  element={<NetworkAccessDenied />}
                />
                <Route
                  path="/:venue_id/*"
                  element={
                    <Suspense fallback={null}>
                      <BroadbandRoute />
                    </Suspense>
                  }
                />
              </>
            )}
            {hostname.startsWith("ps") && (
              <Route
                path="/:venue_id/*"
                element={
                  <Suspense fallback={null}>
                    <PrivateServicesRoute />
                  </Suspense>
                }
              />
            )}
            {(hostname.startsWith("my") || hostname.startsWith("selfcare")) && (
              <Route
                path="/*"
                element={
                  <Suspense fallback={null}>
                    <SelfcareRoute />
                  </Suspense>
                }
              />
            )}
          </>
        )}
      </Routes>
    </>
  );
};

export default RouteConfig;
